import { List, Map } from 'immutable';
import { ActionType as AccessTokenActionType } from '~/AccessToken';

import type { AccessTokenAction } from '~/AccessToken/Action';
import type { LicencesResult } from '~/Api/Customer/DatasourceLicence';
import type { CustomerDataousrceLicenceAction } from '.';

import {
	FAIL_CUSTOMER_DATASOURCE_LICENCES_FETCH,
	RECEIVE_CUSTOMER_DATASOURCE_LICENCES,
	REQUEST_CUSTOMER_DATASOURCE_LICENCES,
} from './ActionTypes';

type StoredValue = {
	request: Promise<Request>;
	isFetching: boolean;
	licenses: List<LicencesResult>;
};

enum StateKeys {
	byCustomerId = 'byCustomerId',
	byCustomerIdAndDatasource = 'byCustomerIdAndDatasource',
}

export type State = Map<StateKeys, Map<string, StoredValue>>;

export default function reducer(
	state: State = createState(),
	action: CustomerDataousrceLicenceAction | AccessTokenAction
): State {
	switch (action.type) {
		case REQUEST_CUSTOMER_DATASOURCE_LICENCES:
			return state.setIn(buildCustomerLicensesKey(action.customerId), {
				request: action.request,
				isFetching: true,
				licenses: List<LicencesResult>(),
			});

		case RECEIVE_CUSTOMER_DATASOURCE_LICENCES:
			let newState = state;
			action.licenses.forEach((licence) => {
				const key = buildCustomerAndDatasourceLicensesKey(action.customerId, licence.datasource);
				newState = newState.setIn(key, licence);
			});

			return newState.updateIn(buildCustomerLicensesKey(action.customerId), (licenses) => ({
				...(licenses as List<LicencesResult>),
				isFetching: false,
				licenses: action.licenses,
			}));

		case FAIL_CUSTOMER_DATASOURCE_LICENCES_FETCH:
			return state.deleteIn(buildCustomerLicensesKey(action.customerId));

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

function createState(): State {
	return Map<StateKeys, Map<string, StoredValue>>([
		[StateKeys.byCustomerId, Map<string, StoredValue>()] as [StateKeys.byCustomerId, Map<string, StoredValue>],
		[StateKeys.byCustomerIdAndDatasource, Map<string, StoredValue>()] as [
			StateKeys.byCustomerIdAndDatasource,
			Map<string, StoredValue>
		],
	]);
}

export function buildCustomerLicensesKey(customerId: string): string[] {
	return [StateKeys.byCustomerId, customerId];
}

export function buildCustomerAndDatasourceLicensesKey(customerId: string, datasource: string): string[] {
	return [StateKeys.byCustomerIdAndDatasource, customerId, datasource];
}
